

import { motion } from 'framer-motion';
import { XCircle, CheckCircle } from 'lucide-react';

export default function HowItWorks() {
  return (
    <section id="how-it-works" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-4">See it in action</h2>
          <p className="text-lg text-text-muted">From frustration to flow.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16">
          {/* BEFORE */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 opacity-75"
          >
            <div className="text-red-500 font-bold mb-8 flex items-center gap-2">
              <XCircle size={24} />
              WITHOUT IANOS
            </div>
            
            <div className="space-y-6 relative">
              <div className="absolute left-6 top-6 bottom-6 w-0.5 bg-gray-100 -z-10"></div>
              
              <div className="flex gap-6 items-start">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex-shrink-0 flex items-center justify-center text-gray-500 font-bold text-sm">1</div>
                <div>
                  <h4 className="font-semibold text-primary">Bug ticket created</h4>
                  <p className="text-sm text-text-muted">Missing reproduction steps & severity</p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="w-12 h-12 bg-red-50 rounded-full flex-shrink-0 flex items-center justify-center text-red-500 font-bold text-sm">2</div>
                <div>
                  <h4 className="font-semibold text-primary">Reaches developer</h4>
                  <p className="text-sm text-text-muted">Developer stops current task to investigate</p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="w-12 h-12 bg-red-50 rounded-full flex-shrink-0 flex items-center justify-center text-red-500 font-bold text-sm">3</div>
                <div>
                  <h4 className="font-semibold text-primary">Back-and-forth ensues</h4>
                  <p className="text-sm text-text-muted">"Can you provide repro steps?" &rarr; Wait 2 hours</p>
                </div>
              </div>

              <div className="bg-red-50 p-4 rounded-lg border border-red-100 text-center text-red-700 font-medium">
                Result: 30+ minutes wasted per ticket
              </div>
            </div>
          </motion.div>

          {/* WITH IANOS */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white p-8 rounded-2xl shadow-xl border border-blue-100 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-accent to-blue-400"></div>
            <div className="text-accent font-bold mb-8 flex items-center gap-2">
              <CheckCircle size={24} />
              WITH IANOS
            </div>

            <div className="space-y-6 relative">
              <div className="absolute left-6 top-6 bottom-6 w-0.5 bg-blue-50 -z-10"></div>

              <div className="flex gap-6 items-start">
                <div className="w-12 h-12 bg-blue-50 rounded-full flex-shrink-0 flex items-center justify-center text-accent font-bold text-sm">1</div>
                <div>
                  <h4 className="font-semibold text-primary">Bug ticket created</h4>
                  <p className="text-sm text-text-muted">Tries to move to "Ready for Dev"</p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="w-12 h-12 bg-accent rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-sm">2</div>
                <div className="w-full">
                  <h4 className="font-semibold text-primary mb-2">IANOS intervenes gently</h4>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 text-sm">
                    <p className="font-medium text-primary mb-1">To help the team, this needs:</p>
                    <ul className="list-disc pl-4 text-text-muted">
                      <li>Reproduction steps (min 25 chars)</li>
                      <li>Severity level (P0-P3)</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="w-12 h-12 bg-blue-50 rounded-full flex-shrink-0 flex items-center justify-center text-accent font-bold text-sm">3</div>
                <div>
                  <h4 className="font-semibold text-primary">Developer starts immediately</h4>
                  <p className="text-sm text-text-muted">All context is present from minute one.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
